import React from 'react';

/**
 * The donor skeleton pieces (owner call 2026-10-23). Every loading placeholder
 * in the donor apps is composed from these, so a block's height, radius and
 * pulse are decided once.
 *
 * Each piece takes the height of the REAL control it stands in for, read from
 * the same tier-2 dial that control reads (`--cs-size-control` for a tile or a
 * CTA, `--cs-input-height` for a field, `--cs-size-segment-track` for the
 * Once/Repeat track, a text role's line height for a line of text). That is
 * what keeps the frame from jumping when the real form replaces it.
 *
 * Widths are fixed fractions, never random, so screenshots are stable.
 * Every piece is `aria-hidden`; the one announcement per surface is
 * `SkeletonStatus`.
 */

/** The block recipe: the sunken neutral, pulsing (animations.css). */
export const SKELETON_BLOCK_CLASS = 'skeleton-item skeleton-wave';

const SHAPE_CLASS = {
  bar: 'rounded-[var(--cs-skeleton-radius)]',
  control: 'rounded-[var(--cs-control-radius)]',
  pill: 'rounded-[var(--cs-pill-radius)]',
  flush: '',
};

export function SkeletonBlock({ className = '', shape = 'bar', style }) {
  return (
    <div
      aria-hidden="true"
      className={`${SKELETON_BLOCK_CLASS} ${SHAPE_CLASS[shape] ?? SHAPE_CLASS.bar} ${className}`.trim()}
      style={style}
    />
  );
}

/**
 * The surface's single live region: `role="status"`, polite, a visually hidden
 * sentence, and the blocks inside it hidden from assistive technology.
 */
export function SkeletonStatus({ label, as: Tag = 'div', className = '', style, children, ...rest }) {
  return (
    <Tag role="status" aria-live="polite" aria-label={label} className={className} style={style} {...rest}>
      <span className="sr-only">{label}, please wait...</span>
      {children}
    </Tag>
  );
}

/**
 * A text line at its role's line height: the block is the line box, so a
 * paragraph of skeleton lines is exactly as tall as the paragraph.
 */
export function SkeletonLine({ leading = 'var(--cs-leading-20)', className = 'w-full' }) {
  return <SkeletonBlock className={className} style={{ height: leading }} />;
}

/** Several lines of running text, `gap` apart. Widths are fixed, in order. */
const DEFAULT_LINE_WIDTHS = ['w-full', 'w-11/12', 'w-full', 'w-3/4'];
export function SkeletonTextLines({
  count = 4,
  leading = 'var(--cs-leading-20)',
  gap = '',
  widths = DEFAULT_LINE_WIDTHS,
  className = '',
}) {
  return (
    <div className={`flex w-full flex-col ${gap} ${className}`.trim()} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        // Each line is its line box with a shorter bar centred in it, so the
        // lines read as lines and the paragraph keeps the paragraph's height.
        <div key={i} className="flex items-center" style={{ height: leading }}>
          <SkeletonBlock
            className={widths[i % widths.length]}
            style={{ height: `calc(${leading} - var(--cs-space-2))` }}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * The form card's 52 title strip, drawn with the real strip's own class so the
 * fill, edge and height are the strip's. The title is one bar on the strip's
 * line height; a chevron Back is never shown (the first step has none).
 */
export function SkeletonTitleStrip({ titleWidth = 'w-24' }) {
  return (
    <div className="w-full" aria-hidden="true">
      <div className="cs-form-title-strip">
        <SkeletonLine leading="var(--cs-title-strip-line-height)" className={titleWidth} />
      </div>
    </div>
  );
}

/** A field label: one bar on the label's 20 line, 8 above the control. */
export function SkeletonLabel({ className = 'w-16' }) {
  return (
    <div className="pb-[var(--cs-space-2)]">
      <SkeletonLine className={className} />
    </div>
  );
}

/** The Once / Repeat track. The real track IS the sunken fill, so it is one block. */
export function SkeletonSegmented() {
  return <SkeletonBlock shape="control" className="h-[var(--cs-size-segment-track)] w-full" />;
}

/** The 2×2 preset amount grid (StandardAmountButtons): control-height tiles, 12 apart. */
export function SkeletonAmountTiles({ count = 4 }) {
  return (
    <div className="grid w-full grid-cols-2 gap-[var(--cs-space-3)] pb-[var(--cs-space-1)]" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonBlock key={i} shape="control" className="h-[var(--cs-size-control)]" />
      ))}
    </div>
  );
}

/**
 * A stacked option tile — a giving level, a sponsorship group, a ticket row —
 * drawn as the tile's own outline with its text lines inside, at the tile's
 * padding, so its height is the real tile's whatever the line count.
 *
 * `trailing` puts a block at the end of the first line (the price) or, with
 * `stepper`, a stepper-sized pill on the right of the whole row (a ticket).
 */
export function SkeletonOptionTile({
  titleWidth = 'w-1/2',
  detailWidth = 'w-3/4',
  withDetail = true,
  trailing = true,
  stepper = false,
}) {
  return (
    <div
      aria-hidden="true"
      className="box-border flex w-full items-center gap-[var(--cs-space-3)] rounded-[var(--cs-control-radius)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] px-[var(--cs-space-4)] py-[var(--cs-space-3)]"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[var(--cs-space-1)]">
        <div className="flex items-center justify-between gap-[var(--cs-space-2)]">
          <SkeletonLine leading="var(--cs-leading-24)" className={titleWidth} />
          {trailing && !stepper && <SkeletonLine leading="var(--cs-leading-24)" className="w-14 shrink-0" />}
        </div>
        {withDetail && <SkeletonLine className={detailWidth} />}
      </div>
      {stepper && (
        <SkeletonBlock shape="pill" className="h-[var(--cs-size-stepper-touch)] w-20 shrink-0 sm:h-[var(--cs-size-stepper)]" />
      )}
    </div>
  );
}

const TILE_WIDTHS = [
  ['w-1/2', 'w-3/4'],
  ['w-2/5', 'w-2/3'],
  ['w-3/5', 'w-1/2'],
  ['w-1/3', 'w-3/5'],
];

/** A stack of option tiles, 8 apart (the levels' and the groups' `space-y-2`). */
export function SkeletonOptionTiles({ count = 3, gap = 'gap-[var(--cs-space-2)]', ...tile }) {
  return (
    <div className={`flex w-full flex-col ${gap}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonOptionTile
          key={i}
          titleWidth={TILE_WIDTHS[i % TILE_WIDTHS.length][0]}
          detailWidth={TILE_WIDTHS[i % TILE_WIDTHS.length][1]}
          {...tile}
        />
      ))}
    </div>
  );
}

/**
 * A text field or a select at the input height. `topGap` is the amount
 * field's own 4 top margin; `topPad` is the 8 FundsDropdown keeps above its
 * label when a form does not pass its own container class (levels,
 * sponsorships).
 */
export function SkeletonField({ label = false, labelWidth = 'w-12', topGap = false, topPad = false }) {
  return (
    <div
      className={`w-full ${topGap ? 'mt-[var(--cs-space-1)]' : ''} ${topPad ? 'pt-[var(--cs-space-2)]' : ''}`.trim()}
      aria-hidden="true"
    >
      {label && <SkeletonLabel className={labelWidth} />}
      <SkeletonBlock shape="control" className="h-[var(--cs-input-height)] w-full" />
    </div>
  );
}

/**
 * The primary CTA, pinned to the bottom of the card body the way
 * FormActionButtons is (`mt-auto`), at the CTA's control height. It carries
 * `form-action-buttons` so the overlay pane's sticky footer rule
 * (animations.css) places it exactly where the real button sits.
 */
export function SkeletonCta({ className = '' }) {
  return (
    <div className={`form-action-buttons mt-auto flex flex-col gap-[var(--cs-space-2)] ${className}`.trim()} aria-hidden="true" data-skeleton-cta="">
      <SkeletonBlock shape="control" className="h-[var(--cs-size-control)] w-full" />
    </div>
  );
}

/** The campaign hero, 16:9. `flush` drops the radius for a hero that sits on a card edge. */
export function SkeletonHero({ flush = false, className = '' }) {
  return <SkeletonBlock shape={flush ? 'flush' : 'bar'} className={`aspect-video w-full ${className}`.trim()} />;
}

/** "By <organisation>": the small logo tile and one name bar on the 20 line. */
export function SkeletonByline({ logo = true, nameWidth = 'w-40', logoClassName = '' }) {
  return (
    <div className="flex items-center gap-[var(--cs-space-2)]" aria-hidden="true">
      {logo && <SkeletonBlock className={`size-[var(--cs-size-logo-sm)] shrink-0 ${logoClassName}`.trim()} />}
      <SkeletonLine className={nameWidth} />
    </div>
  );
}

/**
 * The fundraising bar: the progress track, then its stat row. The row's box is
 * the raised figure's 28 line; the bar inside it is a 20 text line.
 */
export function SkeletonProgress({ statWidth = 'w-3/5' }) {
  return (
    <div className="flex w-full flex-col gap-[var(--cs-space-2)]" aria-hidden="true">
      <SkeletonBlock shape="pill" className="h-[var(--cs-size-progress)] w-full" />
      <div className="flex h-[var(--cs-leading-28)] items-center">
        <SkeletonLine className={statWidth} />
      </div>
    </div>
  );
}

/** A summary row ("1 item  $64.00", "0 tickets  $0.00"): a subtle top edge, 12 above its line. */
export function SkeletonSummaryRow() {
  return (
    <div
      className="flex items-center justify-between border-t border-[color:var(--cs-border-control-subtle)] pt-[var(--cs-space-3)]"
      aria-hidden="true"
    >
      <SkeletonLine className="w-16" />
      <SkeletonLine leading="var(--cs-leading-22)" className="w-14" />
    </div>
  );
}

/** The supporters row: a few overlapping avatars and a count line. */
export function SkeletonSupportersRow() {
  return (
    <div className="flex items-center gap-[var(--cs-space-2)]" aria-hidden="true">
      <div className="flex -space-x-2">
        {[0, 1, 2].map((i) => (
          <SkeletonBlock
            key={i}
            shape="pill"
            className="size-[var(--cs-size-avatar)] border-2 border-[color:var(--cs-surface-0)]"
          />
        ))}
      </div>
      <SkeletonLine className="w-28" />
    </div>
  );
}

/** SectionHeader ("About", "Location"): its 20 label line, 8 above the subtle 1px rule. */
export function SkeletonSectionHeader({ className = 'w-14' }) {
  return (
    <div className="flex flex-col gap-[var(--cs-space-2)]" aria-hidden="true">
      <SkeletonLine className={className} />
      <div className="h-px shrink-0 bg-[color:var(--cs-border-control-subtle)]" />
    </div>
  );
}

/** The About section the way ClampedAbout lays it out: header, clamped lines, Learn more. */
export function SkeletonAbout({ lines = 4, leading = 'var(--cs-leading-22)', learnMore = true }) {
  return (
    <div className="flex flex-col gap-[var(--cs-space-3)]" aria-hidden="true">
      <SkeletonSectionHeader />
      <div className="flex flex-col items-start gap-[var(--cs-space-2)]">
        <SkeletonTextLines count={lines} leading={leading} />
        {learnMore && <SkeletonLine className="w-20" />}
      </div>
    </div>
  );
}
